import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Col from '@loggi/ui/components/col';
import get from 'lodash/get';
import { locationModel } from 'app/app.models';
import MainView from 'shared/main-view/main-view.component';
import callMutation from 'shared/relay/mutations/call-mutation';
import UserFeedbackAlert from 'shared/alert/user-feedback-alert/user-feedback-alert.component';
import emoji from 'images/sad.png';
import { CONFLICT_EXPLANATION } from 'tracker.routes';
import {
  CLOSE_BUTTON_VALUE,
  FEEDBACK_ALERT_ERROR_TITLE,
  RETRY_BUTTON_VALUE
} from 'tracker.constants';
import {
  CONFIRM_DELIVERY_ERROR_DESCRIPTION,
  CONFIRM_ORIGINAL_ADDRESS_ERROR_DESCRIPTION
} from '../../../package.constants';
import { DELIVERY_CONFIRMATION_TITLE } from './confirm-delivery.constants';
import ConfirmDeliveryHeader from '../confirm-delivery-header.component';
import ConfirmDeliveryMain from './confirm-delivery-main.component';
import ConfirmDeliveryButton from './confirm-delivery-button.component';
import ConfirmedDeliveryMessage from '../../confirmed-delivery-message/confirmed-delivery-message.component';
import confirmDeliveryWithoutAuthMutation from '../../confirm-delivery/confirm-delivery-without-auth.mutation';

// This component is called by new-confirm-delivery
// It is used to inform the receiver about the events that occur when a DNR is created.
export class ConfirmDelivery extends Component {
  constructor(props) {
    super(props);

    const { wasDeliveredByRecipient, location } = this.props;

    const { state } = location;

    const shouldConfirmPackageDelivery =
      get(
        this.props,
        'privatePackageTracker.shouldConfirmPackageDelivery',
        ''
      ) || state.shouldConfirmPackageDelivery;

    this.state = {
      shouldConfirmPackageDelivery,
      wasDeliveredByRecipient,
      userFeedbackAlert: false,
      userFeedbackAlertProps: {
        illustration: emoji,
        title: FEEDBACK_ALERT_ERROR_TITLE,
        description: CONFIRM_ORIGINAL_ADDRESS_ERROR_DESCRIPTION,
        buttonsProps: [{ value: RETRY_BUTTON_VALUE, onClick: () => {} }]
      },
      isConfirmDeliveryButtonsDisabled: false
    };
  }

  // Check if is the flow without Auth
  _checkUuid = () => {
    const uuid = get(this.props, 'location.state.uuid', false);
    return uuid;
  };

  // Call the New-confirm-delivery-header, passing the content title
  _renderHeader = () => {
    const headerProps = {
      onClickBack: () => this._goToPrevScreen(),
      headerText: DELIVERY_CONFIRMATION_TITLE
    };

    return (
      <ConfirmDeliveryHeader
        onClickBack={headerProps.onClickBack}
        headerText={headerProps.headerText}
      />
    );
  };

  _goToPrevScreen = () => {
    const { history } = this.props;
    const {
      location: {
        state: { packageStatusPath, packageStatusSearch }
      }
    } = this.props;
    const { wasDeliveredByRecipient } = this.state;
    return history.replace({
      pathname: `${packageStatusPath}`,
      search: `${packageStatusSearch}`,
      state: {
        wasDelivered: wasDeliveredByRecipient
      }
    });
  };

  // this method is used ro redirect to other pages, passing the information
  // about privatePackageTracker, the next page will be send-contestation-component
  _goToNextScreen = nextPathname => {
    const {
      history,
      location: { pathname }
    } = this.props;

    const {
      location: {
        state: {
          uuid,
          receivedBy,
          recipientInfo,
          shouldConfirmPackageDelivery,
          packageStatusPath,
          packageStatusSearch
        }
      }
    } = this.props;
    return history.replace({
      pathname: `${pathname}/${nextPathname}`,
      state: {
        receivedBy,
        recipientInfo,
        shouldConfirmPackageDelivery,
        uuid,
        previousPage: `${pathname}`,
        packageStatusPath,
        packageStatusSearch
      }
    });
  };

  // Confirm delivery
  // Call mutation and change the state to show confirmation message
  _onClickToConfirmDelivery = () => {
    this.setState({ isConfirmDeliveryButtonsDisabled: true });
    this._onConfirmDeliveryMutation({ wasDeliveredByRecipient: true });
  };

  _onConfirmDeliveryMutation = ({ wasDeliveredByRecipient }) => {
    const uuid = this._checkUuid();

    const variables =
      // Flow without authentication
      {
        input: {
          pdcbrUuid: uuid,
          wasDelivered: wasDeliveredByRecipient
        }
      };

    const callbacks = {
      onSuccess: response => {
        const responseWithoutAuth =
          response.packageTrackerDeliveryConfirmationByRecipientWithoutAuth;

        this._onSuccessConfirmDeliveryMutation(
          responseWithoutAuth,
          wasDeliveredByRecipient
        );
      },
      onFailure: this._onFailureConfirmDeliveryMutation
    };
    return callMutation(
      confirmDeliveryWithoutAuthMutation,
      variables,
      callbacks
    );
  };

  // Handle of the button to close alert in case of mutation failure
  _onClickToCloseAlert = () => {
    this.setState({ userFeedbackAlert: false });
  };

  // Show alert in case of mutation failure
  _onFailureConfirmDeliveryMutation = () => {
    return this.setState({
      isConfirmDeliveryButtonsDisabled: false,
      userFeedbackAlert: true,
      userFeedbackAlertProps: {
        illustration: emoji,
        title: FEEDBACK_ALERT_ERROR_TITLE,
        description: CONFIRM_DELIVERY_ERROR_DESCRIPTION,
        buttonsProps: [
          {
            value: CLOSE_BUTTON_VALUE,
            onClick: this._onClickToCloseAlert
          }
        ]
      }
    });
  };

  // Deny delivery
  // Redirect to next page
  _onClicktoRedirect = () => {
    this._goToNextScreen(CONFLICT_EXPLANATION);
  };

  _onSuccessConfirmDeliveryMutation({ success }, wasDeliveredByRecipient) {
    if (!success) {
      return this._onFailureConfirmDeliveryMutation();
    }
    this.setState({ wasDeliveredByRecipient });
    return this._goToPrevScreen();
  }

  render() {
    const {
      userFeedbackAlert,
      userFeedbackAlertProps,
      wasDeliveredByRecipient,
      shouldConfirmPackageDelivery,
      isConfirmDeliveryButtonsDisabled
    } = this.state;

    const { location } = this.props;
    const recipientName =
      get(this.props, 'privatePackageTracker.recipientInfo.firstName', '') ||
      location.state.recipientInfo.firstName;

    const receivedBy =
      get(this.props, 'privatePackageTracker.receivedBy', '') ||
      location.state.receivedBy;

    return (
      <MainView className="tracker-content classificationMainView">
        {this._renderHeader()}
        <ConfirmDeliveryMain
          recipientName={recipientName}
          receivedBy={receivedBy}
        />

        {shouldConfirmPackageDelivery && wasDeliveredByRecipient === null && (
          <ConfirmDeliveryButton
            onClickToDeny={this._onClicktoRedirect}
            onClickToConfirm={this._onClickToConfirmDelivery}
            isDisabled={isConfirmDeliveryButtonsDisabled}
          />
        )}

        {wasDeliveredByRecipient && (
          <Col gutter="onehalf">
            <ConfirmedDeliveryMessage />
          </Col>
        )}

        {userFeedbackAlert && (
          <UserFeedbackAlert
            illustration={userFeedbackAlertProps.illustration}
            title={userFeedbackAlertProps.title}
            description={userFeedbackAlertProps.description}
            buttonsProps={userFeedbackAlertProps.buttonsProps}
          />
        )}
      </MainView>
    );
  }
}

ConfirmDelivery.displayName = 'ConfirmDelivery';

ConfirmDelivery.propTypes = {
  location: PropTypes.shape(locationModel).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired
  }).isRequired,
  wasDeliveredByRecipient: PropTypes.bool
};

ConfirmDelivery.defaultProps = {
  wasDeliveredByRecipient: null
};

export default ConfirmDelivery;
